import { ajax } from "rxjs/ajax";
import { Observable } from "rxjs";
import { ShopListSuccessResponse } from "../model/ShopListSuccessResponse";
import { ShopSuccessResponse } from "../model/ShopSuccessResponse";
import { ProductCategoryListItem } from "../../product-categories/models/product-category";
import * as queryString from "query-string";
import { HomepageConfigListSuccessResponse } from "../model/HomepageConfigListSuccessResponse";
import { NewsListSuccessResponse } from "../model/NewsListSuccessResponse";
import { NewsSuccessResponse } from "../model/NewsSuccessResponse";
import { OrderListSuccessResponse } from "../model/OrderListSuccessResponse";
import { OrderSuccessResponse } from "../model/OrderSuccessResponse";
import { GetOrderListFilters } from "../model/GetOrderListFilters";
import { GetOrderListOrderBy } from "../model/GetOrderListOrderBy";
import _ from "lodash";
import {
  TimeslotTemaplatesSuccessResponse,
  TimeslotTemplateUpdate,
} from "../model/TimeslotTemplatesSuccessResponse";
import { map } from "rxjs/operators";
import { getAuthAndDefaultHeaders } from "../../../service/apiConsts";

export class ApiShop {
  constructor(private apiEndpoint: string) {}

  list(
    token: string,
    organization: string
  ): Observable<ShopListSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/shops/items`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  listProductCategories(
    token: string,
    organization: string
  ): Observable<{ data: ProductCategoryListItem[] }> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/shop-product-categories`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  get(
    token: string,
    organization: string,
    id: string
  ): Observable<ShopSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/shops/items/${id}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  listTimeslots(
    token: string,
    organization: string,
    id: string
  ): Observable<TimeslotTemaplatesSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/shops/${id}/timeslot-templates`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  updateTimeslot(
    token: string,
    organization: string,
    shopId: string,
    template: TimeslotTemplateUpdate
  ): Observable<TimeslotTemaplatesSuccessResponse> {
    return ajax({
      method: "POST",
      url: `${this.apiEndpoint}/organizations/${organization}/shops/${shopId}/timeslot-templates/update`,
      headers: getAuthAndDefaultHeaders(token),
      body: {
        templateRequests: [
          {
            templateId: template.id,
            ...template,
          },
        ],
      },
    }).pipe(map((x: any) => x.response));
  }

  update(
    token: string,
    organization: string,
    id: string,
    data: any
  ): Observable<any> {
    return ajax({
      method: "PUT",
      url: `${this.apiEndpoint}/organizations/${organization}/shops/items/${id}`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  insert(
    token: string,
    organization: string,
    data: any
  ): Observable<{ id: string }> {
    return ajax({
      method: "POST",
      url: `${this.apiEndpoint}/organizations/${organization}/shops/items`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  updateProductCategory(
    token: string,
    organization: string,
    id: string,
    data: any
  ): Observable<any> {
    return ajax({
      method: "PUT",
      url: `${this.apiEndpoint}/organizations/${organization}/shop-product-categories/${id}`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  insertProductCategory(
    token: string,
    organization: string,
    data: any
  ): Observable<any> {
    return ajax({
      method: "POST",
      url: `${this.apiEndpoint}/organizations/${organization}/shop-product-categories`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  updateProductCategoryOrder(
    token: string,
    organization: string,
    sort: string[]
  ): Observable<any> {
    return ajax({
      method: "PUT",
      url: `${this.apiEndpoint}/organizations/${organization}/shop-product-categories/update-order`,
      body: { sort },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  getProductCategory(
    token: string,
    organization: string,
    id: string
  ): Observable<ShopSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/shop-product-categories/${id}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  updateHomepage(
    token: string,
    organization: string,
    id: string,
    data: any
  ): Observable<any> {
    return ajax({
      method: "PUT",
      url: `${this.apiEndpoint}/organizations/${organization}/homepage-config/${id}`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  insertHomepage(
    token: string,
    organization: string,
    data: any
  ): Observable<{ id: string }> {
    return ajax({
      method: "POST",
      url: `${this.apiEndpoint}/organizations/${organization}/homepage-config`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  listHomepages(
    token: string,
    organization: string
  ): Observable<HomepageConfigListSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/homepage-config`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  deleteHomepage(
    token: string,
    organization: string,
    id: string
  ): Observable<any> {
    return ajax({
      method: "DELETE",
      url: `${this.apiEndpoint}/organizations/${organization}/homepage-config/${id}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  updateNews(
    token: string,
    organization: string,
    id: string,
    data: any
  ): Observable<any> {
    return ajax({
      method: "PUT",
      url: `${this.apiEndpoint}/organizations/${organization}/announcement/${id}`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  insertNews(
    token: string,
    organization: string,
    data: any
  ): Observable<{ id: string }> {
    return ajax({
      method: "POST",
      url: `${this.apiEndpoint}/organizations/${organization}/announcement`,
      body: { ...data },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  deleteNews(token: string, organization: string, id: string): Observable<any> {
    return ajax({
      method: "DELETE",
      url: `${this.apiEndpoint}/organizations/${organization}/announcement/${id}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  listNews(
    token: string,
    organization: string,
    page: number = 1
  ): Observable<NewsListSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/announcement?page=${page}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  getNews(
    token: string,
    organization: string,
    id: string
  ): Observable<NewsSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/announcement/${id}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  listNewsUnassigned(
    token: string,
    organization: string
  ): Observable<NewsListSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/announcement/unassigned`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  listOrders(
    token: string,
    organization: string,
    filters: GetOrderListFilters,
    orderBy: GetOrderListOrderBy,
    page: number = 1
  ): Observable<OrderListSuccessResponse> {
    let clearedFilters = _.pickBy(filters, (x) => !_.isNil(x) && x !== "");
    let clearedOrderBy = _.pickBy(
      { orderBy: orderBy?.column, orderDir: orderBy?.dir },
      (x) => !_.isNil(x) && x !== ""
    );

    const filtersStr = queryString.stringify(
      { ...clearedFilters, ...clearedOrderBy, page },
      { arrayFormat: "bracket" }
    );

    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/order?${filtersStr}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  getOrder(
    token: string,
    organization: string,
    id: string
  ): Observable<OrderSuccessResponse> {
    return ajax({
      method: "GET",
      url: `${this.apiEndpoint}/organizations/${organization}/order/${id}`,
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }

  refundOrder(
    token: string,
    organization: string,
    id: string,
    amount: number
  ): Observable<OrderSuccessResponse> {
    return ajax({
      method: "POST",
      url: `${this.apiEndpoint}/organizations/${organization}/order/${id}/refund`,
      body: { amount },
      headers: getAuthAndDefaultHeaders(token),
    }).pipe(map((x: any) => x.response));
  }
}
